import React, { useState } from 'react';

interface PaymentSetupProps {
  onComplete: () => void;
  onPaymentSelect?: (method: string) => void;
}

const PaymentSetup: React.FC<PaymentSetupProps> = ({ onPaymentSelect }) => {
  const [loading, setLoading] = useState<string | null>(null);

  const handleSelect = async (method: string) => {
    if (!onPaymentSelect) return;
    
    setLoading(method);
    
    // Simulate integration delay/popup for external providers
    if (method === 'Amazon Pay' || method === 'PayPal') {
        await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    onPaymentSelect(method);
    setLoading(null);
  };

  return (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="w-full max-w-2xl bg-white rounded-[2.5rem] shadow-xl p-10 border border-slate-100">
        <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Select Payment</h2>
            <p className="text-slate-400 font-medium mt-2">Choose how you would like to complete your order.</p>
        </div>

        <div className="space-y-6">
            {/* Offline Payment */}
            <button
                onClick={() => handleSelect('Offline Payment')}
                disabled={!!loading}
                className="w-full group relative overflow-hidden bg-slate-50 hover:bg-white border-2 border-slate-100 hover:border-slate-900 p-6 rounded-3xl transition-all duration-300 text-left flex items-center gap-6 shadow-sm hover:shadow-xl"
            >
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-3xl shadow-sm border border-slate-100 group-hover:scale-110 transition-transform">
                    📄
                </div>
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="font-black text-slate-900 text-lg uppercase tracking-tight">Pay Offline / Invoice</h3>
                        {loading === 'Offline Payment' && <div className="animate-spin h-5 w-5 border-2 border-slate-900 border-t-transparent rounded-full"/>}
                    </div>
                    <p className="text-slate-500 text-sm font-medium">Net 30 terms. Pay via bank transfer or check upon delivery.</p>
                </div>
            </button>

            {/* Amazon Pay */}
            <button
                onClick={() => handleSelect('Amazon Pay')}
                disabled={!!loading}
                className="w-full group relative overflow-hidden bg-[#FF9900]/5 hover:bg-[#FF9900]/10 border-2 border-[#FF9900]/20 hover:border-[#FF9900] p-6 rounded-3xl transition-all duration-300 text-left flex items-center gap-6 shadow-sm hover:shadow-xl"
            >
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-3xl shadow-sm border border-[#FF9900]/20 group-hover:scale-110 transition-transform">
                    🛒
                </div>
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="font-black text-[#232F3E] text-lg uppercase tracking-tight">Amazon Pay</h3>
                        {loading === 'Amazon Pay' && <div className="animate-spin h-5 w-5 border-2 border-[#FF9900] border-t-transparent rounded-full"/>}
                    </div>
                    <p className="text-[#232F3E]/70 text-sm font-medium">Fast, secure checkout using your Amazon account.</p>
                </div>
            </button>

            {/* PayPal */}
            <button
                onClick={() => handleSelect('PayPal')}
                disabled={!!loading}
                className="w-full group relative overflow-hidden bg-[#003087]/5 hover:bg-[#003087]/10 border-2 border-[#003087]/20 hover:border-[#003087] p-6 rounded-3xl transition-all duration-300 text-left flex items-center gap-6 shadow-sm hover:shadow-xl"
            >
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-3xl shadow-sm border border-[#003087]/20 group-hover:scale-110 transition-transform">
                    🅿️
                </div>
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="font-black text-[#003087] text-lg uppercase tracking-tight">PayPal</h3>
                        {loading === 'PayPal' && <div className="animate-spin h-5 w-5 border-2 border-[#003087] border-t-transparent rounded-full"/>}
                    </div>
                    <p className="text-[#003087]/70 text-sm font-medium">The safer, easier way to pay online.</p>
                </div>
            </button>
        </div>

        <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-400">
                <span>🔒 Secure 256-bit SSL Encrypted Payment</span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSetup;
